/**
 * INP² Login Screen
 * Dark-themed, branded login for admin, buyers, and demo accounts.
 * No "register" link — all accounts are admin-provisioned.
 */

import React, { useState } from 'react';
import { useAuth } from './AuthContext';

export default function LoginScreen() {
  const { login, error } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const displayError = localError || error;

  const handleSubmit = async e => {
    e.preventDefault();
    setLocalError('');
    if (!username.trim() || !password) {
      setLocalError('Please enter your username and password.');
      return;
    }
    setLoading(true);
    const result = await login(username.trim(), password);
    setLoading(false);
    if (!result.ok) {
      if (result.locked) {
        setLocalError(result.error || 'Account temporarily locked. Try again in a few minutes.');
      } else {
        setLocalError(result.error || 'Invalid credentials');
      }
    }
  };

  const isDemo = username.trim().toUpperCase() === 'DEMO';

  return (
    <div style={styles.page}>
      {/* Background grid pattern */}
      <div style={styles.gridOverlay} />

      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.header}>
          <div style={styles.logoRow}>
            <span style={styles.logoMark}>INP²</span>
            <span style={styles.logoSub}>Executive Cyber Bridge</span>
          </div>
          <p style={styles.tagline}>Decision Intelligence for Executive Leadership</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            {isDemo ? 'Username' : 'Email or Username'}
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder={isDemo ? 'DEMO' : 'Email or username'}
              autoComplete="username"
              autoFocus
              style={styles.input}
              onFocus={e => (e.target.style.borderColor = '#06b6d4')}
              onBlur={e => (e.target.style.borderColor = '#334155')}
            />
          </label>

          <label style={styles.label}>
            Password
            <div style={styles.pwWrap}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                style={{ ...styles.input, paddingRight: 44 }}
                onFocus={e => (e.target.style.borderColor = '#06b6d4')}
                onBlur={e => (e.target.style.borderColor = '#334155')}
              />
              <button type="button" onClick={() => setShowPw(v => !v)} style={styles.eyeBtn}
                tabIndex={-1}>
                {showPw ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </label>

          {displayError && (
            <div style={styles.error}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fca5a5" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{displayError}</span>
            </div>
          )}

          <button type="submit" disabled={loading} style={{
            ...styles.btn,
            opacity: loading ? 0.7 : 1,
          }}>
            {loading ? (
              <span style={styles.btnLoading}>
                <span style={styles.spinner} />
                Signing in…
              </span>
            ) : 'Sign In'}
          </button>
        </form>

        {/* Demo hint */}
        {isDemo && (
          <div style={styles.demoHint}>
            Demo accounts use the password provided by your contact.
          </div>
        )}

        <div style={styles.footer}>
          <span>Access is by invitation only.</span>
        </div>
      </div>

      {/* Bottom attribution */}
      <div style={styles.attribution}>
        INP² Executive Cyber Bridge &nbsp;·&nbsp; Powered by Decision Intelligence
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #020c1b 0%, #0f172a 50%, #020c1b 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    position: 'relative',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    overflow: 'hidden',
  },
  gridOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(6,182,212,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(6,182,212,0.04) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px',
    pointerEvents: 'none',
  },
  card: {
    background: 'rgba(15,23,42,0.95)',
    border: '1px solid #1e293b',
    borderRadius: 14,
    padding: '44px 40px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 0 0 1px rgba(6,182,212,0.06), 0 32px 64px rgba(0,0,0,0.7)',
    position: 'relative',
    zIndex: 1,
  },
  header: {
    textAlign: 'center',
    marginBottom: 36,
  },
  logoRow: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 10,
  },
  logoMark: {
    fontSize: 32,
    fontWeight: 900,
    color: '#06b6d4',
    fontFamily: 'monospace',
    letterSpacing: '-0.03em',
    textShadow: '0 0 24px rgba(6,182,212,0.4)',
  },
  logoSub: {
    fontSize: 13,
    fontWeight: 600,
    color: '#475569',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontFamily: 'monospace',
  },
  tagline: {
    color: '#334155',
    fontSize: 12,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    margin: 0,
    fontFamily: 'monospace',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  label: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  input: {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 7,
    color: '#e2e8f0',
    fontSize: 15,
    padding: '11px 14px',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    width: '100%',
    boxSizing: 'border-box',
  },
  pwWrap: {
    position: 'relative',
  },
  eyeBtn: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
  },
  error: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 7,
    color: '#fca5a5',
    fontSize: 13,
    padding: '10px 13px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    lineHeight: 1.5,
  },
  btn: {
    background: 'linear-gradient(135deg, #0891b2, #0369a1)',
    border: 'none',
    borderRadius: 7,
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    padding: '13px 0',
    cursor: 'pointer',
    letterSpacing: '0.04em',
    marginTop: 4,
    transition: 'opacity 0.15s, transform 0.1s',
    boxShadow: '0 4px 14px rgba(6,182,212,0.25)',
  },
  btnLoading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  spinner: {
    display: 'inline-block',
    width: 14,
    height: 14,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  demoHint: {
    marginTop: 16,
    textAlign: 'center',
    color: '#475569',
    fontSize: 12,
    fontStyle: 'italic',
  },
  footer: {
    marginTop: 24,
    textAlign: 'center',
    color: '#334155',
    fontSize: 12,
  },
  attribution: {
    position: 'relative',
    zIndex: 1,
    marginTop: 32,
    color: '#1e293b',
    fontSize: 11,
    letterSpacing: '0.05em',
    fontFamily: 'monospace',
  },
};
